import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Minus, ChevronDown } from "lucide-react";
import { Reveal, SectionHeading, EASE } from "@/components/shared";
import { PLANS } from "@/data/content";
import { waLink } from "@/lib/site";

const COMPARE_ROWS = [
  ["Visita técnica mensal", true, true, true],
  ["Relatório fotográfico", true, true, true],
  ["Registro das patologias", true, true, true],
  ["Canal direto com engenheiro", true, true, true],
  ["Validação de orçamentos", false, true, true],
  ["Análise técnica de propostas", false, true, true],
  ["Priorização de investimentos", false, true, true],
  ["Planejamento anual", false, true, true],
  ["Reunião técnica trimestral", false, true, true],
  ["Fiscalização de obras", false, false, true],
  ["Controle de qualidade e auditoria", false, false, true],
  ["Certificação da solução executada", false, false, true],
];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export default function Plans() {
  const [showCompare, setShowCompare] = useState(false);

  return (
    <section id="plano-realiza" className="relative scroll-mt-20 overflow-hidden py-24 lg:py-32">
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[600px] w-[900px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold/[0.05] blur-[140px]" />
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHeading
          align="center"
          eyebrow="Plano Condomínio Seguro Realiza®"
          title={<>A engenharia que protege <span className="text-gold-grad">as decisões do síndico</span></>}
          sub="Manutenção orientada, decisões respaldadas e patrimônio preservado — escolha o nível de proteção do seu condomínio."
        />

        <div className="mt-20 grid items-stretch gap-6 lg:grid-cols-3 lg:gap-5">
          {PLANS.map((plan, i) => (
            <Reveal key={plan.name} delay={i * 0.1} className="h-full">
              <motion.article
                whileHover={{ y: -8 }}
                transition={{ duration: 0.35, ease: EASE }}
                data-testid={`plan-card-${slug(plan.name)}`}
                className={`relative flex h-full flex-col p-8 transition-colors duration-500 ${
                  plan.highlighted
                    ? "border border-gold/60 bg-gradient-to-b from-navy-700 via-navy-800 to-navy-900 shadow-[0_30px_80px_-30px_rgba(212,175,55,0.3)]"
                    : "border border-white/10 bg-navy-800/40 hover:border-gold/30"
                }`}
              >
                {plan.badge && (
                  <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full gold-band px-5 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-navy-950">
                    {plan.badge}
                  </span>
                )}
                <h3 className="font-display text-2xl font-bold uppercase tracking-wide text-white">{plan.name}</h3>
                <p className="mt-1.5 text-xs text-slate-500">{plan.tagline}</p>
                <p className="mt-6 flex items-baseline gap-1.5">
                  <span className={`font-display text-5xl font-extrabold ${plan.highlighted ? "text-gold-grad" : "text-white"}`}>
                    {plan.price}
                  </span>
                  <span className="font-mono text-xs text-slate-500">{plan.period}</span>
                </p>
                <div className="my-6 h-px w-full gold-line" />
                <ul className="flex-1 space-y-3">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-slate-300">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" strokeWidth={2.5} />
                      {f}
                    </li>
                  ))}
                </ul>
                <a
                  href={waLink(`Olá! Quero o ${plan.name} do Condomínio Seguro Realiza® para o meu condomínio.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`plan-cta-${slug(plan.name)}`}
                  className={`mt-8 inline-flex items-center justify-center rounded-full py-3.5 font-display text-sm font-bold tracking-[0.14em] transition-all duration-300 ${
                    plan.highlighted
                      ? "bg-gold text-navy-950 hover:bg-gold-bright hover:shadow-[0_0_30px_rgba(212,175,55,0.4)]"
                      : "border border-gold/40 text-gold hover:bg-gold/10"
                  }`}
                >
                  {plan.highlighted ? "QUERO O PLANO GESTÃO" : `QUERO O ${plan.name}`}
                </a>
              </motion.article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-12 text-center">
            <button
              onClick={() => setShowCompare(!showCompare)}
              data-testid="compare-plans-button"
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.24em] text-gold transition-colors hover:text-gold-bright"
            >
              {showCompare ? "OCULTAR COMPARAÇÃO" : "COMPARAR PLANOS"}
              <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${showCompare ? "rotate-180" : ""}`} />
            </button>
          </div>
        </Reveal>

        <AnimatePresence>
          {showCompare && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="overflow-hidden"
              data-testid="plans-comparison-table"
            >
              <div className="mt-8 overflow-x-auto border border-white/10">
                <table className="w-full min-w-[640px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-white/10 bg-navy-800/70">
                      <th className="px-5 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Recurso</th>
                      {["Essencial", "Gestão", "Blindado"].map((n) => (
                        <th key={n} className="px-5 py-4 text-center font-display text-base font-bold uppercase tracking-wider text-gold">
                          {n}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {COMPARE_ROWS.map(([label, ...vals]) => (
                      <tr key={label} className="border-b border-white/5 transition-colors hover:bg-white/[0.02]">
                        <td className="px-5 py-3.5 text-slate-300">{label}</td>
                        {vals.map((v, i) => (
                          <td key={i} className="px-5 py-3.5 text-center">
                            {v ? <Check className="mx-auto h-4 w-4 text-gold" /> : <Minus className="mx-auto h-4 w-4 text-slate-700" />}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
